import { HOST_URL } from '../config'
import { HttpStatus } from '../constants/http-status.enum'
import type { ApiResponse } from './index'

export type PushMetric =
    | 'cooling_unit_active_time'
    | 'cooling_unit_inactive_time'
    | 'pump_active_time'
    | 'pump_inactive_time'
    | 'boiler_active_time'
    | 'boiler_inactive_time'
    | 'fan_active_time'
    | 'fan_inactive_time'
    | 'circulation_pump_active_time'
    | 'circulation_pump_inactive_time'

export interface PushRule {
    id: number
    userId: number
    clientId: string
    metric: PushMetric
    thresholdSeconds: number
    cooldownMinutes: number
    label?: string | null
    enabled: boolean
    lastTriggeredAt?: string | null
    createdAt: string
    updatedAt: string
}

export interface PushMetricOption {
    value: PushMetric
    label: string
}

function ensureOk<T>(response: Response, result: ApiResponse<T>): ApiResponse<T> {
    if (response.status !== HttpStatus.Ok) throw new Error(result.message)
    return result
}

export async function getPushPublicKey(): Promise<ApiResponse<{ publicKey: string }>> {
    const response = await fetch(`${HOST_URL}push/public-key`, { credentials: 'include' })
    const result = (await response.json()) as ApiResponse<{ publicKey: string }>
    return ensureOk(response, result)
}

export async function subscribePush(subscription: PushSubscription): Promise<ApiResponse<null>> {
    const response = await fetch(`${HOST_URL}push/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ subscription }),
    })
    const result = (await response.json()) as ApiResponse<null>
    return ensureOk(response, result)
}

export async function unsubscribePush(endpoint: string): Promise<ApiResponse<null>> {
    const response = await fetch(`${HOST_URL}push/unsubscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ endpoint }),
    })
    const result = (await response.json()) as ApiResponse<null>
    return ensureOk(response, result)
}

export async function sendPushTest(clientId?: string): Promise<ApiResponse<null>> {
    const response = await fetch(`${HOST_URL}push/test`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ clientId }),
    })
    const result = (await response.json()) as ApiResponse<null>
    return ensureOk(response, result)
}

export async function listPushMetrics(): Promise<ApiResponse<PushMetricOption[]>> {
    const response = await fetch(`${HOST_URL}push/metrics`, { credentials: 'include' })
    const result = (await response.json()) as ApiResponse<PushMetricOption[]>
    return ensureOk(response, result)
}

export async function listPushRules(clientId: string): Promise<ApiResponse<PushRule[]>> {
    const response = await fetch(`${HOST_URL}push/rules?clientId=${encodeURIComponent(clientId)}`, { credentials: 'include' })
    const result = (await response.json()) as ApiResponse<PushRule[]>
    return ensureOk(response, result)
}

export async function createPushRule(input: {
    clientId: string
    metric: PushMetric
    thresholdSeconds: number
    cooldownMinutes?: number
    label?: string
}): Promise<ApiResponse<PushRule>> {
    const response = await fetch(`${HOST_URL}push/rules`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(input),
    })
    const result = (await response.json()) as ApiResponse<PushRule>
    return ensureOk(response, result)
}

export async function updatePushRule(
    ruleId: number,
    input: Partial<{
        clientId: string
        metric: PushMetric
        thresholdSeconds: number
        cooldownMinutes: number
        enabled: boolean
        label: string
    }>,
): Promise<ApiResponse<PushRule>> {
    const response = await fetch(`${HOST_URL}push/rules/${ruleId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(input),
    })
    const result = (await response.json()) as ApiResponse<PushRule>
    return ensureOk(response, result)
}

export async function deletePushRule(ruleId: number): Promise<ApiResponse<null>> {
    const response = await fetch(`${HOST_URL}push/rules/${ruleId}`, {
        method: 'DELETE',
        credentials: 'include',
    })
    const result = (await response.json()) as ApiResponse<null>
    return ensureOk(response, result)
}
